import { FormControl, FormLabel, Stack } from "@chakra-ui/react";
import { Button, Input, Modal, Radio, RadioGroup, useToast, useTranslation } from "@familyzone/component-library";
import React from "react";
import CriteriaSelector, { CriteriaTypes } from "../../modules/criteria/CriteriaSelector";
import { IpObject, MacObject } from "../../modules/criteria/criteriaTypes";
import { getObjects } from "../filtering/ApiHelpers";
import { FirewallRule } from "./FirewallRule";

interface IFirewallRuleModalProps {
  handleSubmit: (rule?: FirewallRule) => void;
  existingRule: FirewallRule;
  isOpen: boolean;
  closeModal: () => void;
  create: boolean;
  busy: boolean;
}

const FirewallRuleModal: React.FC<IFirewallRuleModalProps> = ({ handleSubmit, existingRule, isOpen, closeModal, create, busy }) => {
  const { t } = useTranslation();

  const [ipObjects, setIpObjects] = React.useState<IpObject[]>([]);
  const [macObjects, setMacObjects] = React.useState<MacObject[]>([]);
  const [rule, setRule] = React.useState<FirewallRule>(existingRule);

  const { errorToast } = useToast();

  const populateObjects = React.useCallback(async () => {
    try {
      const data = await Promise.all([getObjects()]);
      setIpObjects(data[0].filter((obj): obj is IpObject => obj.type === 1 || obj.type === 0));
      setMacObjects(data[0].filter((obj): obj is MacObject => obj.type === 4));
    } catch (err) {
      errorToast({ title: "Failed to load. Please refresh the page", isClosable: true });
    }
  }, [errorToast]);

  React.useEffect(() => {
    setRule(existingRule);
  }, [existingRule]);

  React.useEffect(() => {
    void populateObjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sourceOptions: Partial<CriteriaTypes> = {
    device: "Interface",
    ipv4: "Network",
    "ipv4.address": "IP Address",
    "ipv4.singlealias": "IP Address Object",
    "ipv4.range": "Network Range",
    protocol: "Protocol",
    multiprotocol: "Multiple Protocols",
    "transport.port": "Port",
    "transport.portrange": "Port Range",
  };

  const destinationOptions: Partial<CriteriaTypes> = {
    device: "Interface",
    ipv4: "Network",
    "ipv4.address": "IP Address",
    "ipv4.singlealias": "IP Address Object",
    "ipv4.range": "Network Range",
    "transport.port": "Port",
    "transport.portrange": "Port Range",
  };

  return (
    <>
      <Modal
        onClose={() => closeModal()}
        size="md"
        isOpen={isOpen}
        headerText={create ? t("Create Policy") : t("Edit Policy")}
        contentProps={{ style: { overflowX: "hidden", overflowY: "scroll" } }}
      >
        <Stack spacing="sp12" mt="sp8">
          <FormControl>
            <FormLabel fontWeight="bold">Policy Name</FormLabel>
            <Input defaultValue={existingRule.comment} onChange={(e) => setRule({ ...rule, comment: e.target.value })} />
          </FormControl>
          <FormControl>
            <FormLabel fontWeight="bold">Source</FormLabel>
            <CriteriaSelector
              customOptions={sourceOptions}
              selected={rule.source_criteria}
              errors={[]}
              ipObjects={ipObjects}
              macObjects={macObjects}
              onChange={(selected) => setRule({ ...rule, source_criteria: selected })}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontWeight="bold">Destination</FormLabel>
            <CriteriaSelector
              customOptions={destinationOptions}
              selected={rule.destination_criteria}
              errors={[]}
              ipObjects={ipObjects}
              macObjects={macObjects}
              onChange={(selected) => setRule({ ...rule, destination_criteria: selected })}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontWeight="bold">Action</FormLabel>
            <RadioGroup
              direction="row"
              onChange={(value) => setRule({ ...rule, action: Number(value) })}
              defaultValue={String(existingRule.action)}
            >
              <Radio value="0">Block</Radio>
              <Radio value="1">Accept</Radio>
              <Radio value="2">Bypass</Radio>
            </RadioGroup>
          </FormControl>
          <FormControl>
            <Button disabled={busy} variant="primary" onClick={() => handleSubmit(rule)}>
              Save Policy
            </Button>
          </FormControl>
        </Stack>
      </Modal>
    </>
  );
};

export default FirewallRuleModal;
